import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import { getYouTubeThumbnail } from '../../../../lib/videoUtils';
import { ProgressIndicator } from './ProgressIndicator';

interface FileGridItemShellProps {
  /**
   * File name/title
   */
  name: string;
  /**
   * Thumbnail URL or preview URL
   */
  thumbnailUrl?: string;
  /**
   * YouTube URL for imports
   */
  youtubeUrl?: string;
  /**
   * Status text to display at bottom
   */
  statusText: string;
  /**
   * Upload/import progress (0-100) or undefined for indeterminate spinner
   */
  progress?: number;
  /**
   * Whether to show percentage text with progress
   */
  showPercentage?: boolean;
  /**
   * Optional content to render in the body
   */
  children?: React.ReactNode;
  /**
   * File type for preview rendering
   */
  fileType?: string;
  /**
   * Whether this is a failed import/upload
   */
  isError?: boolean;
  /**
   * Callback to dismiss a failed import
   */
  onDismiss?: () => void;
}

/**
 * Unified grid view item shell used for uploads, imports, and processing files
 * Provides consistent styling and structure across all grid view items
 */
export const FileGridItemShell: React.FC<FileGridItemShellProps> = ({
  name,
  thumbnailUrl,
  youtubeUrl,
  statusText,
  progress,
  showPercentage = false,
  children,
  fileType,
  isError = false,
  onDismiss,
}) => {
  const isVideo = fileType?.startsWith('video/');
  const isAudio = fileType?.startsWith('audio/');
  const ytThumbnail = getYouTubeThumbnail(youtubeUrl);
  const displayThumbnail = ytThumbnail || thumbnailUrl;

  // For audio files, don't show thumbnail - just white background with progress
  const shouldShowThumbnail = !isAudio && displayThumbnail;

  return (
    <div className="relative flex flex-col h-full border border-border rounded-lg overflow-hidden bg-white dark:bg-background opacity-70 hover:opacity-80 transition-opacity cursor-not-allowed">
      {/* Dismiss button for failed imports */}
      {isError && onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="absolute top-2 right-2 z-10 p-1 rounded-full bg-background/80 hover:bg-background border border-border shadow-sm transition-colors"
          title="Dismiss"
        >
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      )}
      {/* Thumbnail area with loader overlay */}
      <div className="relative">
        {shouldShowThumbnail ? (
          <div className="w-full aspect-video bg-gray-50 dark:bg-gray-900/50 relative">
            {isVideo && thumbnailUrl ? (
              <video
                src={thumbnailUrl}
                className="w-full h-full object-cover"
                muted
              />
            ) : (
              <img
                src={displayThumbnail}
                alt={name}
                className="w-full h-full object-cover"
              />
            )}
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ) : (
          <div className="w-full aspect-video bg-white dark:bg-white" />
        )}
        {/* Progress indicator or error icon overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isError ? (
            <div className="flex flex-col items-center gap-2">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>
          ) : (
            <ProgressIndicator
              progress={progress}
              size="lg"
              variant={shouldShowThumbnail ? 'dark' : 'light'}
              showPercentage={showPercentage}
            />
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="p-3 flex-grow flex flex-col">
        <h3 className="text-sm font-medium line-clamp-2 leading-snug mb-1.5 min-h-[2.5rem] text-gray-700 dark:text-gray-300">
          {name}
        </h3>
        <div className="mt-auto">
          <p
            className={`text-xs ${isError ? 'text-destructive' : 'text-gray-500 dark:text-gray-500'}`}
          >
            {statusText}
          </p>
        </div>
        {children}
      </div>
    </div>
  );
};
